import { useEffect, useState, type MouseEvent } from 'react';
import clsx from 'clsx';
import type { DayMediaSrc } from '@/platform/dayMedia';
import styles from './DayMediaThumb.module.css';

type LoadState = 'loading' | 'loaded' | 'error';

export function DayMediaThumb({
  source,
  className,
  imgClassName,
  onClick,
  preferTransform = false,
}: {
  source: DayMediaSrc;
  className?: string;
  imgClassName?: string;
  onClick?: (event: MouseEvent) => void;
  preferTransform?: boolean;
}) {
  const [state, setState] = useState<LoadState>('loading');
  const [useFallback, setUseFallback] = useState(!preferTransform || !source.transformUrl);
  const src = useFallback || !source.transformUrl ? source.url : source.transformUrl;

  useEffect(() => {
    setState('loading');
    setUseFallback(!preferTransform || !source.transformUrl);
  }, [source.url, source.transformUrl, preferTransform]);

  useEffect(() => {
    // Revoke blob URLs only when this thumb unmounts or source changes.
    const url = source.url;
    const kind = source.kind;
    return () => {
      if (kind === 'blob' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };
  }, [source.url, source.kind]);

  return (
    <button
      type="button"
      className={clsx(styles.wrap, className)}
      onClick={onClick}
      data-media-id={source.mediaId}
      data-source-kind={source.kind}
      data-load-state={state}
    >
      {state === 'loading' && <span className={styles.skeleton} aria-hidden />}
      {state === 'error' && (
        <span className={styles.error} aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="9" cy="10" r="1.5" fill="currentColor" />
            <path d="M3 16l5-4 4 3 4-5 5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      )}
      <img
        key={src}
        src={src}
        alt=""
        className={clsx(styles.img, imgClassName, state !== 'loaded' && styles.imgHidden)}
        loading="eager"
        decoding="async"
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0) setState('loaded');
          else setState('error');
        }}
        onError={() => {
          if (!useFallback && source.transformUrl && source.url !== source.transformUrl) {
            setUseFallback(true);
            setState('loading');
            return;
          }
          setState('error');
        }}
      />
    </button>
  );
}
